// Deterministic stress-board generator (spec §10): mixed stickies and shapes
// laid out on a jittered grid, with z keys appended above `afterZ`. Most
// stickies and some shapes carry short rich text so the text cache is exercised.
import type { BoardObject, Card, ChecklistItem, RichText, RichTextRun, ShapeKind } from './types.ts';
import { keysBetween } from './fractional-index.ts';
import { newId } from './ids.ts';
import { CARD, CARD_COLORS, singleLineCardHeight } from './cards.ts';

export const STICKY_COLORS = ['#FFE066', '#FFB3C7', '#A8E6CF', '#9AD0F5', '#FFCB8E', '#D4B8FF'];
const SHAPE_FILLS = ['#FFFFFF', '#E8F0FE', '#FDECEA', '#E6F4EA', '#FEF7E0', 'transparent'];
const SHAPE_STROKES = ['#1A1A1A', '#4262FF', '#D93025', '#188038'];
const KINDS: ShapeKind[] = ['rect', 'ellipse', 'diamond', 'triangle'];
const WORDS = ('idea plan ship user test design review sprint goal metric launch copy research budget ' +
  'roadmap feedback risk scope build fix blocker onboarding pricing churn retention interview ' +
  'prototype survey persona journey backlog estimate deadline demo').split(' ');

function stressText(rand: () => number): RichText {
  const word = () => WORDS[Math.floor(rand() * WORDS.length)]!;
  const blocks: RichText['blocks'] = [];
  const count = 1 + Math.floor(rand() * 3);
  for (let b = 0; b < count; b++) {
    const n = 1 + Math.floor(rand() * 6);
    const words = Array.from({ length: n }, word);
    const runs: RichTextRun[] = [];
    const marked = rand() < 0.3 ? Math.floor(rand() * n) : -1;
    words.forEach((w, i) => {
      const t = (i ? ' ' : '') + w;
      if (i === marked) runs.push(rand() < 0.5 ? { t, b: 1 } : { t, i: 1 });
      else runs.push({ t });
    });
    blocks.push(rand() < 0.2 ? { list: 'bullet', runs } : { runs });
  }
  return { blocks };
}

function mulberry32(seed: number): () => number {
  return () => {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface StressOptions {
  seed?: number;
  afterZ?: string | null;
  createdBy?: string;
  origin?: [number, number];
  /** Cards with checklists only (spec §12, phase 7 budget), instead of stickies and shapes. */
  cards?: boolean;
}

/** A card with a short title and 2–6 one-line items (so its height needs no measuring). */
function stressCard(base: Omit<BoardObject, 'type' | 'w' | 'h'>, rand: () => number): Card {
  const word = () => WORDS[Math.floor(rand() * WORDS.length)]!;
  const n = 2 + Math.floor(rand() * 5);
  const orders = keysBetween(null, null, n);
  const items: Record<string, ChecklistItem> = {};
  for (let i = 0; i < n; i++) items[newId()] = { text: `${word()} ${word()}`, done: rand() < 0.4, order: orders[i]! };
  const card: Card = {
    ...base, type: 'card', w: CARD.width, h: 0, title: `${word()} ${word()}`,
    color: CARD_COLORS[Math.floor(rand() * CARD_COLORS.length)]!, items, assignees: [],
  };
  card.h = singleLineCardHeight(card);
  return card;
}

export function generateStressBoard(count: number, opts: StressOptions = {}): BoardObject[] {
  const rand = mulberry32(opts.seed ?? 1);
  const pick = <T>(xs: readonly T[]): T => xs[Math.floor(rand() * xs.length)]!;
  const [ox, oy] = opts.origin ?? [0, 0];
  const createdBy = opts.createdBy ?? 'stress';
  const cols = Math.ceil(Math.sqrt(count));
  const cellX = opts.cards ? 320 : 260, cellY = opts.cards ? 280 : 260;
  const zs = keysBetween(opts.afterZ ?? null, null, count);
  const out: BoardObject[] = [];

  for (let i = 0; i < count; i++) {
    const x = ox + (i % cols) * cellX + rand() * 40;
    const y = oy + Math.floor(i / cols) * cellY + rand() * 40;
    const rotation = rand() < 0.1 && !opts.cards ? (rand() - 0.5) * 0.6 : 0;
    const base = { id: newId(), x, y, rotation, z: zs[i]!, createdBy };
    if (opts.cards) {
      out.push(stressCard(base, rand));
    } else if (rand() < 0.6) {
      const text = rand() < 0.8 ? stressText(rand) : { blocks: [] };
      out.push({ ...base, type: 'sticky', w: 200, h: 200, color: pick(STICKY_COLORS), text });
    } else {
      out.push({
        ...base, type: 'shape', kind: pick(KINDS),
        w: 80 + rand() * 140, h: 80 + rand() * 140,
        fill: pick(SHAPE_FILLS), stroke: pick(SHAPE_STROKES), strokeWidth: 2,
        ...(rand() < 0.4 ? { text: stressText(rand) } : {}),
      });
    }
  }
  return out;
}
